// Đơn POS trên hệ thống (API V2): kết quả sau thanh toán lịch hẹn, hoặc tra đơn theo mã để xem và thu nợ (2b + 4b)
import { useEffect, useMemo, useRef, useState } from 'react';
import { Modal, message } from 'antd';
import { AlertTriangle, CalendarClock, CheckCircle2, FileText, HandCoins, Loader2, Plus, Printer, Search, X } from 'lucide-react';
import { getPosOrder, type PosOrderDetail } from 'api/PosV2';
import { METHOD_LABEL } from '../data';
import { PAYMENT_STATUS_LABEL, POS_STATUS_LABEL, collectOrderRemote, customerFromApi, methodFromApi, orderFromApi, remoteMethods } from '../remote';
import { formatPhone, money, printReceipt, usePosAuth } from '../utils';
import PaymentModal from './PaymentModal';

export interface RemoteOrderResult {
  detail: PosOrderDetail | null;
  orderCode: string;
  orderId: number;
  change: number;
  partialError?: string;
}

interface Props {
  open: boolean;
  /** Có: hiện kết quả vừa thanh toán. Không: chế độ tra cứu đơn */
  result?: RemoteOrderResult | null;
  onClose: () => void;
  closeLabel?: string;
  /** Chế độ tra cứu: nạp sẵn đơn này khi mở */
  orderKey?: string | number | null;
  /** Gọi sau khi thu nợ / thanh toán thêm thành công */
  onPaid?: () => void;
}

const PAY_STATUS_CLS: Record<string, string> = {
  PAID: 'bg-green-50 text-green-700 border-green-200',
  PARTIALLY_PAID: 'bg-amber-50 text-amber-700 border-amber-200',
  UNPAID: 'bg-red-50 text-red-600 border-red-200',
};

export default function RemoteOrderModal({ open, result, onClose, closeLabel, orderKey, onPaid }: Props) {
  const { userName, can } = usePosAuth();
  const [query, setQuery] = useState('');
  const [detail, setDetail] = useState<PosOrderDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [change, setChange] = useState(0);
  const [collectOpen, setCollectOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    setDetail(result?.detail || null);
    setChange(result?.change || 0);
    setError(result && !result.detail ? `Đã tạo đơn ${result.orderCode} nhưng chưa tải được chi tiết đơn` : '');
    setQuery(!result && orderKey ? String(orderKey) : '');
    if (!result && orderKey) load(orderKey);
    else if (!result) setTimeout(() => inputRef.current?.focus(), 100);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, result, orderKey]);

  const load = async (key: string | number) => {
    const k = String(key).trim();
    if (!k) return;
    setLoading(true);
    setError('');
    const res = await getPosOrder(k);
    setLoading(false);
    if (!res.ok || !res.data) {
      setError(res.message || 'Không tìm thấy đơn hàng');
      // Tra mã khác thất bại thì không để đơn cũ hiện kèm lỗi
      if (!result) setDetail(null);
      return;
    }
    setDetail(res.data);
    // Mở theo ID đơn thì hiện lại mã đơn cho dễ đọc
    if (!result) setQuery(res.data.order_code);
  };

  const printable = useMemo(() => (detail ? orderFromApi(detail) : null), [detail]);
  const print = (size: 'K80' | 'A4') => {
    if (!printable || !detail) return;
    printReceipt(printable.order, size, { change, transactions: printable.transactions, branchName: detail.branch?.name || '', reprint: !result });
  };

  const isInstallment = detail?.payment_type === 'INSTALLMENT' || !!detail?.installments?.length;
  const collect = async (payments: Parameters<typeof collectOrderRemote>[2]) => {
    if (!detail) return;
    const res = await collectOrderRemote(detail.order_id, detail.debt_amount, payments, userName, isInstallment);
    if (!res.ok) {
      message.error(res.error || 'Thu nợ thất bại');
      return;
    }
    if (res.error) message.warning(res.error);
    else message.success('Đã ghi nhận thanh toán');
    setCollectOpen(false);
    setChange(res.change);
    await load(detail.order_id);
    onPaid?.();
  };

  const debt = detail?.debt_amount || 0;
  // Trả góp: gợi ý thu nợ đến hạn, chưa có thì đợt kế tiếp (thu ngân vẫn sửa được, tối đa toàn bộ nợ)
  const dueNow = detail?.due_amount || 0;
  const next = detail?.next_installment || null;
  const suggested = isInstallment ? (dueNow > 0 ? dueNow : next?.remaining || 0) : 0;
  const collectHint = isInstallment
    ? dueNow > 0 ? `Trả góp – nợ đến hạn ${money(dueNow)}đ` : next ? `Trả góp – đợt ${next.no} hạn ${fmtDay(next.due_date)}: ${money(next.remaining)}đ` : undefined
    : undefined;
  // Trả góp: nói rõ khoản đang thu phủ những đợt nào (server phân bổ theo thứ tự đợt), phần còn lại là theo lịch chứ không phải thiếu
  const installmentNote = (entered: number, remaining: number): string | undefined => {
    if (!isInstallment || entered <= 0) return undefined;
    if (remaining <= 0) return 'Thu hết các đợt còn lại – đơn chuyển Hoàn thành';
    const total = detail?.installments?.length || 0;
    const pending = (detail?.installments || []).filter(i => i.remaining > 0).sort((a, b) => a.no - b.no);
    let left = entered;
    const full: number[] = [];
    let partial: { no: number; short: number } | null = null;
    for (const i of pending) {
      if (left <= 0) break;
      if (left >= i.remaining) { full.push(i.no); left -= i.remaining; }
      else { partial = { no: i.no, short: i.remaining - left }; left = 0; }
    }
    const fullTxt = !full.length ? '' : full.length === 1 ? `đợt ${full[0]}/${total}` : `đợt ${full[0]}–${full[full.length - 1]}/${total}`;
    if (partial) return `Thu ${fullTxt ? `${fullTxt} và ` : ''}một phần đợt ${partial.no} (thiếu ${money(partial.short)}đ) – còn ${money(remaining)}đ`;
    return fullTxt ? `Thu ${fullTxt} – còn ${money(remaining)}đ theo lịch các đợt sau` : undefined;
  };
  const title = result
    ? (isInstallment ? 'Đã tạo đơn trả góp' : debt > 0 ? 'Đã tạo đơn – khách còn nợ' : 'Thanh toán thành công')
    : 'Tra đơn / Thu nợ';

  return (
    <Modal open={open} onCancel={onClose} footer={null} closable={false} maskClosable={!result} centered width={680} destroyOnHidden
      styles={{ content: { padding: 0, overflow: 'hidden' } }}>
      <div className="flex max-h-[90vh] flex-col">
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-3.5">
          <h2 className="flex items-center gap-2 text-lg font-bold text-slate-800">
            {result && <CheckCircle2 className={`h-6 w-6 ${debt > 0 ? 'text-amber-500' : 'text-green-600'}`} />}{title}
          </h2>
          <button onClick={onClose} className="flex h-10 w-10 items-center justify-center rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-600" aria-label="Đóng">
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-5">
          {!result && (
            <form onSubmit={e => { e.preventDefault(); load(query); }} className="flex gap-2">
              <div className="relative flex-1">
                <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center"><Search className="h-4 w-4 text-slate-400" /></span>
                <input ref={inputRef} value={query} onChange={e => setQuery(e.target.value.toUpperCase())} placeholder="Mã đơn (VD: POS-Q1-261021-0003) hoặc ID đơn"
                  className="h-11 w-full rounded-lg border border-slate-300 pl-9 pr-3 font-mono text-sm outline-none focus:border-[#3e91ab] focus:ring-2 focus:ring-[#3e91ab]/20" />
              </div>
              <button type="submit" disabled={!query.trim() || loading} className="flex h-11 items-center gap-1.5 rounded-lg bg-[#357f96] px-4 text-sm font-bold text-white hover:bg-[#2c6b7f] disabled:opacity-50">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />} Tra cứu
              </button>
            </form>
          )}

          {result?.partialError && (
            <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" /> {result.partialError}
            </div>
          )}
          {error && <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}
          {loading && result && <div className="flex justify-center py-6"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>}

          {detail && (
            <>
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <div className="font-mono text-base font-bold text-slate-800">{detail.order_code}</div>
                  <div className="text-sm text-slate-600">
                    {detail.customer?.name || 'Khách lẻ'}{detail.customer?.phone ? ` · ${formatPhone(String(detail.customer.phone).replace(/\D/g, ''))}` : ''}
                  </div>
                  <div className="text-xs text-slate-500">{detail.branch?.name}{detail.appointment_id ? ` · Lịch hẹn #${detail.appointment_id}` : ''}</div>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${PAY_STATUS_CLS[detail.payment_status] || 'border-slate-200 bg-slate-50 text-slate-600'}`}>
                    {PAYMENT_STATUS_LABEL[detail.payment_status] || detail.payment_status}
                  </span>
                  <span className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-2.5 py-0.5 text-xs font-semibold text-slate-600">
                    {/* Đơn bán lẻ (không có lịch hẹn) không vào phòng làm dịch vụ */}
                    {!detail.appointment_id && detail.pos_status === 'COMPLETED_PAYMENT' ? 'Đã thu tiền' : POS_STATUS_LABEL[detail.pos_status] || detail.pos_status}
                  </span>
                </div>
              </div>

              <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200 text-sm">
                {detail.items.map((it, i) => (
                  <li key={it.id ?? i} className="flex justify-between gap-3 px-3 py-2">
                    <span className="min-w-0">
                      <span className="block font-medium text-slate-800">{it.product_name}{it.quantity > 1 ? ` ×${it.quantity}` : ''}</span>
                      <span className="text-xs text-slate-500">
                        {money(it.price)}{it.discount ? ` · giảm ${money(it.discount)}/đv` : ''}{it.staff_name ? ` · KTV: ${it.staff_name}` : ''}
                      </span>
                    </span>
                    <span className="shrink-0 font-semibold text-slate-700">{money(it.sub_total)}</span>
                  </li>
                ))}
              </ul>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1 text-sm">
                  <Row label="Tạm tính" value={money(detail.subtotal)} />
                  {printable && printable.order.totals.lineDiscount > 0 && <Row label="Chiết khấu" value={`-${money(printable.order.totals.lineDiscount)}`} />}
                  {detail.order_discount > 0 && <Row label="Chiết khấu đơn" value={`-${money(detail.order_discount)}`} />}
                  {detail.voucher_discount > 0 && <Row label={`Voucher ${detail.voucher_code || ''}`} value={`-${money(detail.voucher_discount)}`} />}
                  {detail.rounding_amount !== 0 && <Row label="Làm tròn" value={money(detail.rounding_amount)} />}
                  <Row label="Tổng đơn" value={`${money(detail.final_amount)}đ`} strong />
                  <Row label="Đã thu" value={money(detail.paid_amount)} />
                  {detail.approver_name && <p className="pt-1 text-xs text-slate-500">Chiết khấu được duyệt bởi {detail.approver_name}{detail.approved_note ? ` – ${detail.approved_note}` : ''}</p>}
                </div>
                <div className="space-y-2">
                  <div className={`rounded-xl p-3 ${debt > 0 ? 'bg-amber-50' : 'bg-green-50'}`}>
                    <div className="text-xs text-slate-500">{debt > 0 ? 'Còn nợ' : 'Tiền thối'}</div>
                    <div className={`text-3xl font-extrabold ${debt > 0 ? 'text-amber-600' : 'text-green-600'}`}>{money(debt > 0 ? debt : change)}đ</div>
                  </div>
                  <ul className="space-y-1 text-xs text-slate-600">
                    {detail.payments.map(p => (
                      <li key={p.id} className="flex justify-between gap-2">
                        <span>
                          {METHOD_LABEL[methodFromApi(p.payment_method)!] || p.payment_method}
                          {p.is_deposit && <span className="ml-1 rounded bg-amber-100 px-1 text-[10px] font-semibold text-amber-800">Cọc</span>}
                          {p.transaction_code && <span className="ml-1 font-mono text-slate-400">{p.transaction_code}</span>}
                          <span className="block text-slate-400">{p.created_at}{p.cashier_name ? ` · ${p.cashier_name}` : ''}</span>
                        </span>
                        <b className="shrink-0 text-slate-800">{money(p.amount)}</b>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              {!!detail.installments?.length && (
                <div className="rounded-lg border border-slate-200">
                  <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 px-3 py-2 text-sm">
                    <span className="flex items-center gap-1.5 font-semibold text-slate-700"><CalendarClock className="h-4 w-4 text-[#3e91ab]" /> Trả góp {detail.installments.length} đợt</span>
                    <span className="text-xs text-slate-500">
                      {dueNow > 0
                        ? <b className="text-red-600">Nợ đến hạn {money(dueNow)}đ</b>
                        : next ? <>Đợt kế tiếp: <b className="text-slate-700">đợt {next.no} · {fmtDay(next.due_date)} · {money(next.remaining)}đ</b></> : 'Đã thu đủ các đợt'}
                    </span>
                  </div>
                  <table className="w-full text-sm">
                    <thead className="text-xs text-slate-500">
                      <tr className="text-left">
                        <th className="px-3 py-1.5 font-medium">Đợt</th><th className="px-2 py-1.5 font-medium">Hạn</th>
                        <th className="px-2 py-1.5 text-right font-medium">Số tiền</th><th className="px-2 py-1.5 text-right font-medium">Còn lại</th>
                        <th className="px-3 py-1.5 text-right font-medium">Trạng thái</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {detail.installments.map(it => (
                        <tr key={it.id ?? it.no} className={it.no === next?.no ? 'bg-[#ecf6f9]/60' : ''}>
                          <td className="px-3 py-1.5 font-medium text-slate-700">{it.no}</td>
                          <td className="px-2 py-1.5 text-slate-600">{fmtDay(it.due_date)}</td>
                          <td className="px-2 py-1.5 text-right text-slate-700">{money(it.amount)}</td>
                          <td className={`px-2 py-1.5 text-right font-semibold ${it.remaining > 0 ? 'text-slate-800' : 'text-slate-400'}`}>{money(it.remaining)}</td>
                          <td className="px-3 py-1.5 text-right"><InstallmentStatus status={it.status} overdueDays={it.overdue_days} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {detail.note && <p className="rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-600">Ghi chú: {detail.note}</p>}
            </>
          )}
        </div>

        <div className="grid shrink-0 grid-cols-2 gap-2 border-t border-slate-200 bg-slate-50 px-5 py-4 sm:grid-cols-4">
          <button disabled={!detail || !can('print')} onClick={() => print('K80')} className="flex h-12 items-center justify-center gap-2 rounded-lg border border-slate-300 bg-white text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50">
            <Printer className="h-4 w-4" /> In K80
          </button>
          <button disabled={!detail || !can('print')} onClick={() => print('A4')} className="flex h-12 items-center justify-center gap-2 rounded-lg border border-slate-300 bg-white text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50">
            <FileText className="h-4 w-4" /> In A4
          </button>
          <button disabled={!detail || debt <= 0} onClick={() => setCollectOpen(true)} className="flex h-12 items-center justify-center gap-2 rounded-lg border border-amber-300 bg-amber-50 text-sm font-bold text-amber-800 hover:bg-amber-100 disabled:opacity-40">
            <HandCoins className="h-4 w-4" /> Thu nợ
          </button>
          <button onClick={onClose} className="flex h-12 items-center justify-center gap-2 rounded-lg bg-[#357f96] text-sm font-bold text-white hover:bg-[#2c6b7f]">
            {result && <Plus className="h-4 w-4" />}{closeLabel || (result ? 'ĐƠN MỚI' : 'Đóng')}
          </button>
        </div>
      </div>

      {detail && (
        <PaymentModal open={collectOpen} mode="collect" amountDue={debt} orderCode={detail.order_code}
          suggestedAmount={suggested} hint={collectHint} collectNote={installmentNote}
          methods={remoteMethods(detail.customer?.wallet_balance !== undefined && detail.customer?.wallet_balance !== null)}
          customer={customerFromApi(detail.customer)}
          onClose={() => setCollectOpen(false)} onConfirm={collect} />
      )}
    </Modal>
  );
}

/** "YYYY-MM-DD" → "DD/MM/YYYY" */
const fmtDay = (iso?: string | null) => (iso && /^\d{4}-\d{2}-\d{2}/.test(iso) ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}` : iso || '');

const InstallmentStatus = ({ status, overdueDays }: { status: string; overdueDays: number }) => {
  if (status === 'PAID') return <span className="rounded bg-green-50 px-1.5 py-0.5 text-xs font-semibold text-green-700">Đã thu</span>;
  if (status === 'OVERDUE' || overdueDays > 0) return <span className="rounded bg-red-50 px-1.5 py-0.5 text-xs font-semibold text-red-600">Quá hạn {overdueDays} ngày</span>;
  return <span className="rounded bg-slate-100 px-1.5 py-0.5 text-xs font-semibold text-slate-600">Chưa đến hạn</span>;
};

const Row = ({ label, value, strong }: { label: string; value: string; strong?: boolean }) => (
  <div className={`flex justify-between gap-2 ${strong ? 'border-t border-slate-200 pt-1 text-base font-bold text-slate-800' : 'text-slate-600'}`}>
    <span>{label}</span><span className="font-medium">{value}</span>
  </div>
);
